import React from 'react';
import { AlertTriangle, ChevronDown, Info, Calculator } from 'lucide-react';
import { Screenshot } from './Screenshot';
import type { MarketBlock, MarketSection } from '../../types/market';
const noteTone = {
  info: {
    cls: 'border-sky-400/30 bg-sky-400/[0.07] text-sky-100',
    Icon: Info,
    label: 'Importante'
  },
  warn: {
    cls: 'border-amber-400/40 bg-amber-400/[0.08] text-amber-100',
    Icon: AlertTriangle,
    label: 'Atención'
  },
  example: {
    cls: 'border-violet-400/30 bg-violet-400/[0.07] text-violet-100',
    Icon: Calculator,
    label: 'Ejemplo'
  }
};
function Block({
  b
}: {
  b: MarketBlock;
}) {
  switch (b.t) {
    case 'p':
      return <p className="leading-relaxed text-gray-300" data-id="element-942" data-mp="1oxc53sss0esx">{b.text}</p>;
    case 'steps':
      return <ol className="space-y-2.5" data-id="element-943" data-mp="1tvx1fh4wqec3">
          {b.items.map((s, i) => <li key={s} className="flex gap-3" data-id="element-944" data-mp="dip1rualtalg" data-mpi={i}>
              <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#D4AF37]/15 text-xs font-bold text-[#D4AF37]" aria-hidden="true" data-id="element-945" data-mp="dl2vnztjsgrf">
                {i + 1}
              </span>
              <span className="leading-relaxed text-gray-200" data-id="element-946" data-mp="57oh10g5hgds">{s}</span>
            </li>)}
        </ol>;
    case 'list':
      return <ul className="list-disc space-y-1.5 pl-5 marker:text-[#D4AF37]/70" data-id="element-947" data-mp="j891s25mlm9x">
          {b.items.map(s => <li key={s} className="leading-relaxed text-gray-300" data-id="element-948" data-mp="e6th4b7d1hs2">
              {s}
            </li>)}
        </ul>;
    case 'table':
      return <ResponsiveTable head={b.head} rows={b.rows} caption={b.caption} example={b.example} data-id="element-949" data-mp="o72h804h3ncq" />;
    case 'note':
      {
        const t = noteTone[b.tone];
        return <div className={`flex gap-3 rounded-2xl border px-4 py-3 text-sm leading-relaxed ${t.cls}`} data-id="element-950" data-mp="1201r9mlhkpcw">
          <t.Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-951" data-mp="xyq0veusg03n" />
          <p data-id="element-952" data-mp="1bwmxgb98s6ib">
            <span className="sr-only" data-id="element-953" data-mp="1bu0tsmph2b5d">{t.label}: </span>
            {b.text}
          </p>
        </div>;
      }
    case 'shot':
      return <Screenshot id={b.id} data-id="element-954" data-mp="4k5s932hzmes" />;
  }
}

/** Tabla en escritorio; en móvil cada fila se convierte en una tarjeta con sus etiquetas, sin desplazamiento lateral. */
export function ResponsiveTable({
  head,
  rows,
  caption,
  example
}: {
  head: string[];
  rows: string[][];
  caption?: string;
  example?: boolean;
}) {
  return <div className="break-inside-avoid" data-id="element-955" data-mp="ibajbgrsv6di">
      {caption && <p className={`mb-2 text-xs font-semibold ${example ? 'text-violet-300' : 'text-gray-400'}`} data-id="element-956" data-mp="id3pyw2exqli">{caption}</p>}
      <table className="hidden w-full border-collapse text-left text-sm sm:table" data-id="element-957" data-mp="s96ug8kqm1bm">
        <thead data-id="element-958" data-mp="sbb4z9d55ykv">
          <tr className="border-b border-white/15" data-id="element-959" data-mp="wasce8c4p5so">
            {head.map(h => <th key={h} scope="col" className="py-2 pr-4 font-semibold text-white" data-id="element-960" data-mp="w8z6iv9caplq">
                {h}
              </th>)}
          </tr>
        </thead>
        <tbody data-id="element-961" data-mp="2y3pzron4z668">
          {rows.map(r => <tr key={r.join('|')} className="border-b border-white/5 align-top" data-id="element-962" data-mp="330zyxw0buw79">
              {r.map((c, i) => i === 0 ? <th key={i} scope="row" className="py-2.5 pr-4 font-semibold text-gray-100" data-id="element-963" data-mp="2viil5dlf4v8h">
                    {c}
                  </th> : <td key={i} className="py-2.5 pr-4 text-gray-300" data-id="element-964" data-mp="2lre66dvqtadn">
                    {c}
                  </td>)}
            </tr>)}
        </tbody>
      </table>
      <ul className="space-y-2 sm:hidden" data-id="element-965" data-mp="3eh4m8sgxqmce">
        {rows.map(r => <li key={r.join('|')} className="rounded-xl border border-white/10 px-3 py-2.5" data-id="element-966" data-mp="39hiakngr9e5p">
            <p className="font-semibold text-white" data-id="element-967" data-mp="39fp355ybipwn">{r[0]}</p>
            <dl className="mt-1 space-y-1 text-sm" data-id="element-968" data-mp="34h3y8t84fi1v">
              {r.slice(1).map((c, i) => <div key={i} data-id="element-969" data-mp="30g1zsem13a0y" data-mpi={i}>
                  <dt className="inline text-gray-500" data-id="element-970" data-mp="30iwio1pe8ms9">{head[i + 1]}: </dt>
                  <dd className="inline text-gray-300" data-id="element-971" data-mp="1t4tgriytv8cq">{c}</dd>
                </div>)}
            </dl>
          </li>)}
      </ul>
    </div>;
}
export function MarketSectionView({
  section,
  forceOpen = false
}: {
  section: MarketSection;
  forceOpen?: boolean;
}) {
  const body = <div className="space-y-4" data-id="element-972" data-mp="3hqnpeua9au9v">
      {section.blocks.map((b, i) => <Block key={i} b={b} data-id="element-973" data-mp="330ye468g99vv" data-mpi={i} />)}
    </div>;
  if (section.extended) {
    return <section id={section.id} className="scroll-mt-32" data-id="element-974" data-mp="37yi1pwr1ez0h">
        <details open={forceOpen} className="group rounded-2xl border border-white/10 bg-white/[0.02]" data-id="element-975" data-mp="37wx4u0h933zg">
          <summary className="flex cursor-pointer list-none items-center justify-between gap-3 rounded-2xl px-4 py-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37] [&::-webkit-details-marker]:hidden" data-id="element-976" data-mp="3vn15q0320pmr">
            <span data-id="element-977" data-mp="3qozqxrcnvch3">
              <span className="block font-cinzel text-base font-bold text-white md:text-lg" data-id="element-978" data-mp="3qog6o3zjgbzw">{section.title}</span>
              <span className="text-xs text-gray-500" data-id="element-979" data-mp="2b7ftp5zi0pqd">Explicación ampliada</span>
            </span>
            <ChevronDown className="h-5 w-5 shrink-0 text-gray-400 transition-transform duration-200 group-open:rotate-180" aria-hidden="true" data-id="element-980" data-mp="2fc5mglyv1o72" />
          </summary>
          <div className="px-4 pb-4" data-id="element-981" data-mp="22srw71ljasr9">{body}</div>
        </details>
      </section>;
  }
  return <section id={section.id} className="scroll-mt-32" data-id="element-982" data-mp="2hpiz3kuf2mlm">
      <h2 className="mb-3 font-cinzel text-lg font-bold text-white md:text-xl" data-id="element-983" data-mp="2hmwr5k3m652v">{section.title}</h2>
      {body}
    </section>;
}